/**
 * Where this workstation keeps its work, and how to change it.
 *
 * There was no answer to that question anywhere in the product. Everything went
 * to `%LocalAppData%` on the operating-system drive, nothing on any screen said
 * so, and the only way to change it was an environment variable — which is not a
 * feature. A scanning station is bought with a fast disk for exactly this, and an
 * operator had no way to point at it.
 *
 * Two things this deliberately does NOT do. It does not move existing work: the
 * batches already on the old disk stay there, and the screen says so, because
 * telling somebody their scans have been relocated and being wrong is far worse
 * than telling them the change applies to what comes next. And it does not
 * pretend to take effect immediately — the engine, the spool and the database are
 * opened at start-up, so the honest thing is to say a restart is needed rather
 * than to appear to work and half-apply.
 */

import { useCallback, useEffect, useState } from 'react';
import { FolderOpen, HardDrive, Info, LoaderCircle, TriangleAlert } from 'lucide-react';

import type { StorageLocation } from '../shared/desktop-api';
import { InfrastructureArt } from './InfrastructureArt';

const desktop = () => window.capturePointDesktop;

/** "1.4 TB free of 1.8 TB" — the sentence an operator is deciding with. */
export const spoken = (bytes: number | null) => {
  if (bytes === null || !Number.isFinite(bytes)) return null;
  const units = ['bytes', 'KB', 'MB', 'GB', 'TB'];
  let value = bytes;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 100 || unit <= 1 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
};

// standards: allow function-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
export function StorageLocationPanel() {
  const [location, setLocation] = useState<StorageLocation | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [chooseError, setChooseError] = useState('');
  const [choosing, setChoosing] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError('');
    try {
      const api = desktop();
      if (!api) throw new Error('The desktop service is not connected.');
      setLocation(await api.getStorageLocation());
    } catch (reason) {
      setLocation(null);
      setLoadError(
        reason instanceof Error ? reason.message : 'The storage location could not be read.',
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const choose = async () => {
    setChoosing(true);
    setChooseError('');
    try {
      const api = desktop();
      if (!api) throw new Error('The desktop service is not connected.');
      setLocation(await api.chooseStorageLocation());
    } catch (reason) {
      setChooseError(
        reason instanceof Error
          ? reason.message
          : 'A different storage folder could not be chosen.',
      );
    } finally {
      setChoosing(false);
    }
  };

  const free = spoken(location?.freeBytes ?? null);
  const total = spoken(location?.totalBytes ?? null);

  return (
    <section className="card storage-location">
      <InfrastructureArt kind="storage" />
      <header className="storage-location-head">
        <h2>
          <HardDrive size={18} /> Storage folder
        </h2>
      </header>

      <p className="storage-location-path">
        {loading ? (
          <>
            <LoaderCircle className="spin" size={14} /> Reading storage location…
          </>
        ) : (
          (location?.path ?? 'Storage location unavailable')
        )}
      </p>

      {loadError && (
        <div
          className="storage-location-error"
          role="alert"
          data-telemetry="error:storage-load-failed"
        >
          <TriangleAlert size={15} />
          <span>
            <strong>Storage location could not be read</strong>
            <small>{loadError}</small>
          </span>
          <button type="button" className="secondary-button small" onClick={() => void load()}>
            Try again
          </button>
        </div>
      )}

      <p className="storage-location-detail">
        {free && total ? (
          <>
            <strong>{free} free</strong> of {total} on this disk
          </>
        ) : (
          'Documents, corrections, setup, and learning for every job on this computer.'
        )}
      </p>

      {!loading && !loadError && location?.fixedByEnvironment ? (
        <div className="storage-location-locked">
          <button
            type="button"
            className="secondary-button"
            disabled
            aria-describedby="storage-location-lock-reason"
          >
            <FolderOpen size={16} /> Change folder
          </button>
          <p className="storage-location-note" id="storage-location-lock-reason">
            <Info size={18} />
            <span>
              <strong>Set by this app’s launch settings.</strong>
              <br />
              Changing folders is unavailable in this run.
            </span>
          </p>
        </div>
      ) : !loading && !loadError ? (
        <div className="storage-location-actions">
          <button
            type="button"
            className="secondary-button"
            onClick={() => void choose()}
            disabled={choosing}
          >
            {choosing ? <LoaderCircle className="spin" size={14} /> : <FolderOpen size={14} />}
            Choose a different folder
          </button>
        </div>
      ) : null}

      {chooseError && (
        <div
          className="storage-location-error"
          role="alert"
          data-telemetry="error:storage-move-failed"
        >
          <TriangleAlert size={15} />
          <span>
            <strong>Storage folder was not changed</strong>
            <small>{chooseError}</small>
          </span>
          <button
            type="button"
            className="secondary-button small"
            onClick={() => void choose()}
            disabled={choosing}
          >
            Try again
          </button>
        </div>
      )}

      {location?.restartRequired && (
        <p className="storage-location-note restart">
          <Info size={14} />
          <span>
            <strong>Close and reopen Paige to start using it.</strong> Work already saved stays
            where it is - nothing has been moved - and new scans will go to the new folder.
          </span>
        </p>
      )}
    </section>
  );
}
